import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { MdAdd, MdDeleteOutline, MdEditNote } from "react-icons/md";
import moment from "moment";
import styles from "./Dashboard.module.scss";
import useCloudinary from "../../utils/useCloudinary";
import {
  getEvents,
  createEvent,
  updateEvent,
  deleteEvent,
} from "../../utils/network/Crud";

const emptyForm = {
  title: "",
  description: "",
  date: "",
  time: "",
  location: "",
  image: "",
};

const ManageEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const { uploadImage, uploading } = useCloudinary();

  const loadEvents = async () => {
    setLoading(true);
    const result = await getEvents();
    setEvents(result?.data || []);
    setLoading(false);
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setImageFile(null);
    setShowModal(true);
  };

  const openEdit = (event) => {
    setEditingId(event.id);
    const dateVal = event.date?.toDate ? event.date.toDate() : event.date;
    setForm({
      title: event.title || "",
      description: event.description || "",
      date: dateVal ? moment(dateVal).format("YYYY-MM-DD") : "",
      time: event.time || "",
      location: event.location || "",
      image: event.image || "",
    });
    setImageFile(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date) {
      toast.error("Title and date are required");
      return;
    }
    try {
      let imageUrl = form.image;
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }
      const payload = { ...form, image: imageUrl || "", date: new Date(form.date) };
      if (editingId) {
        await updateEvent(editingId, payload);
        toast.success("Event updated");
      } else {
        await createEvent(payload);
        toast.success("Event published");
      }
      setShowModal(false);
      loadEvents();
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteEvent(deleteId);
      toast.success("Event removed");
      setDeleteId(null);
      loadEvents();
    } catch (error) {
      toast.error("Failed to remove event");
    }
  };

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h2>Events</h2>
          <p>Publish upcoming events shown on the public Events page.</p>
        </div>
        <button className={styles.primaryBtn} onClick={openAdd}>
          <MdAdd /> Add Event
        </button>
      </div>

      <div className={styles.panel}>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Image</th>
                <th>Title</th>
                <th>Date</th>
                <th>Location</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5}>Loading…</td>
                </tr>
              ) : events.length === 0 ? (
                <tr>
                  <td colSpan={5}>No events published yet.</td>
                </tr>
              ) : (
                events.map((event) => {
                  const dateVal = event.date?.toDate ? event.date.toDate() : event.date;
                  return (
                    <tr key={event.id}>
                      <td>
                        {event.image ? (
                          <img src={event.image} alt={event.title} className={styles.thumb} />
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{event.title}</td>
                      <td>{dateVal ? moment(dateVal).format("DD MMM YYYY") : "—"}</td>
                      <td>{event.location || "—"}</td>
                      <td>
                        <button className={styles.iconBtn} onClick={() => openEdit(event)}>
                          <MdEditNote />
                        </button>
                        <button
                          className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                          onClick={() => setDeleteId(event.id)}
                        >
                          <MdDeleteOutline />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h3>{editingId ? "Edit Event" : "Add Event"}</h3>
            <form className={styles.formGrid} onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label>Title</label>
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Date</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Time</label>
                <input
                  type="text"
                  placeholder="e.g. 6:00 PM Onwards"
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Location</label>
                <input
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Banner Image</label>
                <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
                {form.image && !imageFile && (
                  <img src={form.image} alt="current" className={styles.thumb} style={{ marginTop: 6 }} />
                )}
              </div>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.primaryBtn} disabled={uploading}>
                  {uploading ? "Saving…" : editingId ? "Save Changes" : "Publish Event"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteId && (
        <div className={styles.modalOverlay} onClick={() => setDeleteId(null)}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h3>Remove Event?</h3>
            <p style={{ color: "#b6acc9", fontSize: 13 }}>This action cannot be undone.</p>
            <div className={styles.modalActions}>
              <button className={styles.secondaryBtn} onClick={() => setDeleteId(null)}>
                Cancel
              </button>
              <button className={`${styles.primaryBtn} ${styles.dangerBtn}`} onClick={handleDelete}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageEvents;
